import {v4 as uuidv4} from 'uuid';

import refreshNoteLinkedGroupActionFunction from 'notebook/notes/groups/action-functions/refreshNoteLinkedGroupActionFunction';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
  NoteLinkedGroup,
  NoteLinkedGroupAdapter,
  NoteLinkedGroupKind,
  NoteLinkedGroupResyncBannerContent,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {SamplesSource} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {isStructuredTable} from 'notebook/TypePredicates';
import {
  AnyStructuredTable,
  isNotePartWithItem,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';
import {MaybePromise} from 'util/Promises';

import {reduceGroupUpdateNotePart} from './util/ContainedNotePartUtil';

export default abstract class GenericNoteLinkedGroupAdapter<
  TGroupMetadata,
  TCreateGroupExtras,
> implements NoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras> {
  kind: NoteLinkedGroupKind;

  constructor(kind: NoteLinkedGroupKind) {
    this.kind = kind;
  }

  abstract getDisplayName(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): string;

  abstract supportsCopyPaste(): boolean;

  getActionFunctions(): GroupActionFunctions<TGroupMetadata, TCreateGroupExtras> {
    return {
      secondary: [refreshNoteLinkedGroupActionFunction],
      overflow: [],
    };
  }

  // This method is final, and should not be overridden by subclasses.
  createGroup(createProps: CreateNoteLinkedGroupProps<TGroupMetadata, TCreateGroupExtras>): Omit<
    NoteLinkedGroup<TGroupMetadata>,
    'cid' | 'containedNoteParts'
  > & {
    containedNoteParts: Array<Omit<NotePart, 'cid'>>;
  } {
    const {name, kind, groupMetadata, isConfig} = createProps;

    return {
      name,
      type: NoteLinkedObjectType.NOTE_LINKED_GROUP,
      kind,
      provenanceUuid: uuidv4(),
      isConfig: isConfig ?? false,
      groupMetadata,
      containedNoteParts: this.createNoteParts(createProps),
    };
  }

  abstract createNoteParts(
    createProps: CreateNoteLinkedGroupProps<TGroupMetadata, TCreateGroupExtras>
  ): Array<Omit<NotePart, 'cid'>>;

  /**
   * Apply all updates to the group given an updated note part.
   *
   * By default, this always replaces the pre-existing notePart with the updatedNotePart.
   *
   * If your group has more complex logic (conditional, async, one note part affecting another), you can
   * override this method. You'll likely want to call reduceGroupUpdateNotePart to still merge in the change
   * like is done in the default case.
   *
   */
  reduceGroup(
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    updatedNotePart: NotePart
  ): MaybePromise<NoteLinkedGroup<TGroupMetadata>> {
    return reduceGroupUpdateNotePart(noteLinkedGroup, updatedNotePart);
  }

  prepareGroupForStore(_noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): void {}

  isGroupResyncRequired(
    _noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    _evaluatedTableMap: OutputTableMap
  ): boolean {
    return false;
  }

  resyncGroupComponentsAsync(
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    _evaluatedTableMap: OutputTableMap
  ): MaybePromise<NoteLinkedGroup<TGroupMetadata>> {
    return noteLinkedGroup;
  }

  getResyncBannerContent(): NoteLinkedGroupResyncBannerContent {
    return {
      title: 'Update required',
      subtitle: 'Apply changes to recalculate.',
      buttonText: 'Apply changes',
    };
  }

  requiresTableEvaluationOnInsert(): boolean {
    return false;
  }

  // Keep in sync with NoteLinkedGroup.KINDS_WITH_ENTRY_LEVEL_EVALUATION on the backend.
  supportsEntryLevelEvaluation(): boolean {
    return false;
  }

  getContainedStructuredTables(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<AnyStructuredTable> {
    const notePartsWithStructuredTables = noteLinkedGroup.containedNoteParts.filter(
      (notePart) => isNotePartWithItem(notePart) && isStructuredTable(notePart.item)
    ) as Array<NotePartWithItem<AnyStructuredTable>>;
    return notePartsWithStructuredTables.map((notePart) => notePart.item);
  }

  allowDeleteGroup(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): {
    canDelete: boolean;
    reason?: string;
  } {
    for (const structuredTable of this.getContainedStructuredTables(noteLinkedGroup)) {
      const result = getStructuredTableAdapters()[structuredTable.kind].allowDeleteTable(structuredTable);
      if (!result.canDelete) {
        return result;
      }
    }
    return {canDelete: true};
  }

  getGroupSampleSources(
    _noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    _evaluatedTableMap: OutputTableMap | null
  ): Array<SamplesSource> {
    return [];
  }

  abstract getGroupSchemas(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<Schema>;
}
